import React, { useEffect, useMemo, useState, useRef } from 'react';
import MedicationItem from '../MedicationItem';
import PreviousPrescriptions from '../PreviousPrescriptions';
import { generatePrescriptionPDF as generatePDF } from '../../utils/pdfGenerator';
import { masterDataServices } from '../../../../api/masterData';
import { useAuth } from '../../../../state/auth/AuthContext';

import { patientAPI } from '../../../../api/patient';
import { profileService } from '../../../../api/user';

const PrescriptionTab = ({
  selectedPatient,
  prescriptionData,
  setPrescriptionData,
  previousPrescriptions,
  loadingPrescriptions,
  loading,
  diseases,
  slogans,
  medicines,
  tests,
  imagingTests,
  loadingMasterData,
  onAddMedication,
  onRemoveMedication,
  onUpdateMedication,
  onSavePrescription
}) => {
  const { user } = useAuth();
  const [testSearch, setTestSearch] = useState('');
  // Local slogans list so we can append newly created slogans
  const [sloganOptions, setSloganOptions] = useState(slogans || []);
  const [doctorProfile, setDoctorProfile] = useState(null);
  
  // Ultra-lightweight disease search - only loads on demand
  const [diseaseInput, setDiseaseInput] = useState('');
  const [diseaseResults, setDiseaseResults] = useState([]);
  const [isDiseaseSearching, setIsDiseaseSearching] = useState(false);
  const [showDiseaseList, setShowDiseaseList] = useState(false);
  const diseaseTimeoutRef = useRef(null);
  const diseaseWrapperRef = useRef(null);

  // Ultra-lightweight debounced disease search - only searches when user types 3+ chars
  const searchDiseases = async (query) => {
    if (!query || query.length < 3) {
      setDiseaseResults([]);
      return;
    }

    try {
      setIsDiseaseSearching(true);
      const response = await masterDataServices.diseases.getActive({ 
        search: query,
        limit: 20,
        sortBy: 'name',
        sortOrder: 'asc'
      });
      const records = Array.isArray(response) ? response : response?.records || response?.data?.records || [];
      setDiseaseResults(records);
    } catch (error) {
      console.error('[PRESCRIPTION_TAB] Disease search failed:', error);
      setDiseaseResults([]);
    } finally {
      setIsDiseaseSearching(false);
    }
  };

  // Handle disease input change with aggressive debouncing
  const handleDiseaseInputChange = (value) => {
    setDiseaseInput(value);
    setShowDiseaseList(true);

    // Clear previous timeout
    if (diseaseTimeoutRef.current) {
      clearTimeout(diseaseTimeoutRef.current);
    }

    // Only search if 3+ characters, with 400ms debounce
    if (value.length >= 3) {
      diseaseTimeoutRef.current = setTimeout(() => {
        searchDiseases(value);
      }, 400);
    } else {
      setDiseaseResults([]);
      setIsDiseaseSearching(false);
    }
  };

  // Close disease list on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (diseaseWrapperRef.current && !diseaseWrapperRef.current.contains(e.target)) {
        setShowDiseaseList(false);
      }
    };
    
    if (showDiseaseList) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDiseaseList]);

  // Debug: Log props on mount and when they change
  useEffect(() => {
    console.log('[PRESCRIPTION_TAB] Props updated:', {
      diseasesCount: diseases?.length,
      medicinesCount: medicines?.length,
      testsCount: tests?.length,
      imagingTestsCount: imagingTests?.length,
      slogansCount: slogans?.length,
      loadingMasterData
    });
  }, [diseases, medicines, tests, imagingTests, slogans, loadingMasterData]);

  useEffect(() => {
    let cancelled = false;
    const fetchDoctorProfile = async () => {
      if (!user?._id) return;
      try {
        const profileResp = await profileService.getProfile();
        if (cancelled) return;
        console.log('[PRESCRIPTION_TAB] Raw profile API response:', profileResp);

        // Extract data from nested structure
        const profileData = profileResp?.data || profileResp;
        console.log('[PRESCRIPTION_TAB] Extracted profileData:', profileData);
        console.log('[PRESCRIPTION_TAB] personalDetails:', profileData?.personalDetails);
        console.log('[PRESCRIPTION_TAB] professionalDetails:', profileData?.professionalDetails);

        setDoctorProfile(profileData || null);
      } catch (err) {
        console.warn('[PRESCRIPTION_TAB] Failed to fetch doctor profile:', err);
      }
    };

    fetchDoctorProfile();
    return () => { cancelled = true; };
  }, [user?._id]);

  const coercePrimitive = (value) => {
    if (value === undefined || value === null) return '';
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number') return String(value);
    if (Array.isArray(value)) {
      for (const entry of value) {
        const coerced = coercePrimitive(entry);
        if (coerced) return coerced;
      }
    } else if (typeof value === 'object') {
      const direct = coercePrimitive(value.value)
        || coercePrimitive(value.label)
        || coercePrimitive(value.name)
        || coercePrimitive(value.display)
        || coercePrimitive(value.text);
      if (direct) return direct;
    }
    return '';
  };

  const deepFindValueByKeys = (root, keyMatchers, maxDepth = 6) => {
    if (!root || typeof root !== 'object') return '';
    const queue = [{ node: root, depth: 0 }];
    const visited = new Set();
    while (queue.length) {
      const { node, depth } = queue.shift();
      if (!node || typeof node !== 'object') continue;
      if (visited.has(node)) continue;
      visited.add(node);
      const entries = Array.isArray(node) ? node.entries() : Object.entries(node);
      for (const [key, value] of entries) {
        if (!Array.isArray(node) && keyMatchers.some((rx) => rx.test(key))) {
          const coerced = coercePrimitive(value);
          if (coerced) return coerced;
        }
        if (value && typeof value === 'object' && depth < maxDepth) {
          queue.push({ node: value, depth: depth + 1 });
        }
      }
    }
    return '';
  };

  const getDoctorData = () => {
    if (!user && !selectedPatient && !doctorProfile) return null;

    // Priority 1: Use doctorProfile from API if available
    if (doctorProfile) {
      const personal = doctorProfile.personalDetails || {};
      const professional = doctorProfile.professionalDetails || {};

      const firstName = personal.firstName || doctorProfile.firstName || '';
      const lastName = personal.lastName || doctorProfile.lastName || '';
      const combined = [firstName, lastName].filter(Boolean).join(' ').trim();
      const fallbackName = doctorProfile.name || '';

      const qualification = professional.qualification || professional.degree || '';
      const registrationNumber = professional.licenseNumber || professional.registrationNumber || '';

      console.log('[PRESCRIPTION_TAB][getDoctorData] Using doctorProfile from API:', {
        firstName,
        lastName,
        combined,
        fallbackName,
        qualification,
        registrationNumber
      });

      return {
        name: combined || fallbackName || 'N/A',
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        qualification: qualification || undefined,
        registrationNumber: registrationNumber || undefined
      };
    }

    // Priority 2: Deep search in all available sources
    const highPrioritySources = [];
    const lowPrioritySources = [];
    const seen = new Set();
    const addSource = (candidate, high = false, label = '') => {
      if (!candidate || typeof candidate !== 'object') return;
      if (seen.has(candidate)) return;
      seen.add(candidate);
      if (high) highPrioritySources.push({ source: candidate, label });
      else lowPrioritySources.push({ source: candidate, label });
    };

    const doctorFromSelected = selectedPatient?.doctor || selectedPatient?.appointment?.doctor || selectedPatient?.queueItem?.doctor;
    addSource(doctorFromSelected, true, 'selectedPatient.doctor/appointment.doctor');
    addSource(doctorFromSelected?.user, true, 'doctorFromSelected.user');
    addSource(doctorFromSelected?.profile, true, 'doctorFromSelected.profile');
    addSource(doctorFromSelected?.details, true, 'doctorFromSelected.details');
    addSource(selectedPatient?.appointment?.doctorDetails, true, 'selectedPatient.appointment.doctorDetails');
    addSource(selectedPatient?.appointmentDoctor, true, 'selectedPatient.appointmentDoctor');
    addSource(selectedPatient?.doctorProfile, true, 'selectedPatient.doctorProfile');

    addSource(user, false, 'user (from AuthContext)');
    addSource(user?.profile, false, 'user.profile');
    addSource(user?.doctorProfile, false, 'user.doctorProfile');
    addSource(user?.details, false, 'user.details');
    addSource(user?.profile?.basicInfo, false, 'user.profile.basicInfo');
    addSource(user?.profile?.professionalInfo, false, 'user.profile.professionalInfo');
    addSource(user?.profile?.doctorInfo, false, 'user.profile.doctorInfo');
    addSource(user?.contact, false, 'user.contact');

    const sources = highPrioritySources.concat(lowPrioritySources);
    if (sources.length === 0) return null;

    console.log('[PRESCRIPTION_TAB][getDoctorData] Available sources:', sources.map(s => s.label));
    console.log('[PRESCRIPTION_TAB][getDoctorData] user object:', user);
    console.log('[PRESCRIPTION_TAB][getDoctorData] doctorProfile state:', doctorProfile);
    console.log('[PRESCRIPTION_TAB][getDoctorData] selectedPatient:', selectedPatient);

    const getValue = (patterns, fieldName = '') => {
      for (const { source, label } of sources) {
        const value = deepFindValueByKeys(source, patterns);
        if (value) {
          console.log(`[PRESCRIPTION_TAB][getDoctorData] Found ${fieldName} from source: ${label}, value:`, value);
          return value;
        }
      }
      console.log(`[PRESCRIPTION_TAB][getDoctorData] ${fieldName} NOT found in any source`);
      return '';
    };

    const first = getValue([/^(first|given)[ _-]?name$/i, /^first$/i, /^given$/i], 'firstName');
    const last = getValue([/^(last|family|surname)[ _-]?name$/i, /^last$/i, /^surname$/i], 'lastName');
    const combined = [first, last].filter(Boolean).join(' ');
    const fallbackName = getValue([/^name$/i, /^full[ _-]?name$/i, /^display[ _-]?name$/i, /^doctorName$/i], 'fallbackName');
    const qualification = getValue([/qualification/i, /degree/i, /specialization/i, /certification/i], 'qualification');
    const registrationNumber = getValue([/(registration|reg)[ _-]?(number|no|id)/i, /license[ _-]?(number|no)/i, /medical[ _-]?registration/i, /mci[ _-]?number/i], 'registrationNumber');

    console.log('[PRESCRIPTION_TAB][getDoctorData] Extracted values => firstName:', first, 'lastName:', last, 'combined:', combined, 'fallback:', fallbackName, 'qualification:', qualification, 'registrationNumber:', registrationNumber);

    return {
      name: combined || fallbackName || 'N/A',
      firstName: first || undefined,
      lastName: last || undefined,
      qualification: qualification || undefined,
      registrationNumber: registrationNumber || undefined
    };
  };

  // Clear form only when patient actually changes (avoid clearing on tab switch/remount)
  const patientKey = selectedPatient?.patient?._id || selectedPatient?.patient || '';
  const prevPatientKeyRef = useRef(null);

  useEffect(() => {
    if (!patientKey) return;

    // First render: just record the patient key; do NOT reset.
    if (prevPatientKeyRef.current === null) {
      prevPatientKeyRef.current = patientKey;
      return;
    }

    if (prevPatientKeyRef.current !== patientKey) {
      prevPatientKeyRef.current = patientKey;
      setPrescriptionData({
        language: 'E',
        adultChild: 'adult',
        appointmentType: 'New',
        indoorOutdoor: 'outdoor',
        knownCaseDisease: '',
        items: [{
          medicine: '',
          name: '',
          instruction: '',
          days: '5',
          dose: '',
          frequency: '',
          timing: {
            morning: false,
            noon: false,
            evening: false,
            night: false
          },
          route: '',
          modified: false
        }],
        tests: [],
        imagingTests: [{ test: '', selectedViews: [], instructions: '' }],
        foodAdvice: '',
        sloganRef: '',
        flags: {
          changeDays: false,
          addToDiary: false,
          duplicatePrint: false,
          printFormat: 'A4'
        }
      });
      setSloganText('');
      setDiseaseInput('');
      setDiseaseResults([]);
    }
  }, [patientKey, setPrescriptionData]);

  // Function to clear the prescription form
  const handleClearPrescription = () => {
    if (window.confirm('Are you sure you want to clear the prescription form?')) {
      setPrescriptionData({
        language: 'E',
        adultChild: 'adult',
        appointmentType: 'New',
        indoorOutdoor: 'outdoor',
        knownCaseDisease: '',
        items: [{
          medicine: '',
          name: '',
          instruction: '',
          days: '5',
          dose: '',
          frequency: '',
          timing: {
            morning: false,
            noon: false,
            evening: false,
            night: false
          },
          route: '',
          modified: false
        }],
        tests: [],
        imagingTests: [{ test: '', selectedViews: [], instructions: '' }],
        foodAdvice: '',
        sloganRef: '',
        flags: {
          changeDays: false,
          addToDiary: false,
          duplicatePrint: false,
          printFormat: 'A4'
        }
      });
      setSloganText('');
      setDiseaseInput('');
      setDiseaseResults([]);
    }
  };

  useEffect(() => {
    setSloganOptions(slogans || []);
  }, [slogans]);
  const [savingSlogan, setSavingSlogan] = useState(false);
  const [sloganText, setSloganText] = useState('');
  const [showSloganDropdown, setShowSloganDropdown] = useState(false);
  const [sloganBoxRef, setSloganBoxRef] = useState(null);
  const [loadingSlogans, setLoadingSlogans] = useState(false);
  const currentSlogan = useMemo(() => {
    return (sloganOptions || []).find(s => s._id === (prescriptionData?.sloganRef || ''));
  }, [sloganOptions, prescriptionData?.sloganRef]);

  // Helper to extract records from service response and refresh slogans from server to ensure persistence
  const extractRecords = (resp) => {
    if (Array.isArray(resp)) return resp;
    if (resp && Array.isArray(resp.records)) return resp.records;
    if (resp && resp.data && Array.isArray(resp.data.records)) return resp.data.records;
    return [];
  };

  const refreshSlogansFromServer = async () => {
    try {
      setLoadingSlogans(true);
      const server = await masterDataServices.slogans.getActive({ limit: 500, sortBy: 'createdAt', sortOrder: 'desc' });
      const records = extractRecords(server);
      if (records.length) setSloganOptions(records);
      return records;
    } catch (err) {
      console.error('Failed to refresh slogans from server', err);
      return [];
    } finally {
      setLoadingSlogans(false);
    }
  };

  // Initial fetch to ensure slogans are loaded from backend even if parent didn't pass them
  useEffect(() => {
    refreshSlogansFromServer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep text in sync with selected id
  useEffect(() => {
    setSloganText(currentSlogan?.slogan || '');
  }, [currentSlogan?._id]);

  // Close slogan dropdown on outside click
  useEffect(() => {
    if (!showSloganDropdown) return;
    const onDocClick = (e) => {
      if (sloganBoxRef && !sloganBoxRef.contains(e.target)) setShowSloganDropdown(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showSloganDropdown, sloganBoxRef]);

  // Commit logic: type-to-select, create, or update with zero extra clicks
  const commitSlogan = async () => {
    const text = (sloganText || '').trim();
    // If empty, clear selection
    if (!text) {
      if (prescriptionData.sloganRef) setPrescriptionData({ ...prescriptionData, sloganRef: '' });
      return;
    }
    const existing = (sloganOptions || []).find(s => (s.slogan || '').trim().toLowerCase() === text.toLowerCase());
    try {
      setSavingSlogan(true);
      if (existing) {
        // Select existing
        if (prescriptionData.sloganRef !== existing._id) {
          setPrescriptionData({ ...prescriptionData, sloganRef: existing._id });
        }
        // ensure latest list
        await refreshSlogansFromServer();
      } else if (currentSlogan) {
        // Update current selection's text
        await masterDataServices.slogans.update(currentSlogan._id, { slogan: text, isActive: currentSlogan.isActive ?? true });
        await refreshSlogansFromServer();
      } else {
        // Create new and select
        const created = await masterDataServices.slogans.create({ slogan: text, isActive: true });
        const records = await refreshSlogansFromServer();
        const idToSelect = created?._id || (records.find(r => (r.slogan || '').trim().toLowerCase() === text.toLowerCase())?._id);
        if (idToSelect) setPrescriptionData({ ...prescriptionData, sloganRef: idToSelect });
      }
    } catch (err) {
      console.error('Slogan commit failed', err);
      alert('Could not save slogan. Please try again.');
    } finally {
      setSavingSlogan(false);
    }
  };
  const filteredTests = useMemo(() => {
    const q = testSearch.trim().toLowerCase();
    if (!q) return tests || [];
    return (tests || []).filter(t => {
      const name = (t.name || '').toLowerCase();
      const code = (t.code || '').toLowerCase();
      const cat = (t.category || '').toLowerCase();
      return name.includes(q) || code.includes(q) || cat.includes(q);
    });
  }, [testSearch, tests]);

  // Group tests by category (based on filtered list)
  const normalizeCategory = (c) => (c && String(c).trim()) || 'Uncategorized';
  const testsByCategory = useMemo(() => {
    const map = {};
    (filteredTests || []).forEach(test => {
      const cat = normalizeCategory(test.category);
      if (!map[cat]) map[cat] = [];
      map[cat].push(test);
    });
    return map;
  }, [filteredTests]);

  // Collapsible categories state: store categories that are collapsed
  const [collapsedCategories, setCollapsedCategories] = useState(new Set());
  
  // Initialize all categories as collapsed by default
  useEffect(() => {
    const allCategories = Object.keys(testsByCategory);
    if (allCategories.length > 0) {
      setCollapsedCategories(new Set(allCategories));
    }
  }, [Object.keys(testsByCategory).join(',')]);
  
  const isCollapsed = (category) => collapsedCategories.has(category);
  const toggleCategory = (category) => {
    setCollapsedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category); else next.add(category);
      return next;
    });
  };

  const selectAllInCategory = (category) => {
    const selected = new Set(prescriptionData.tests || []);
    (testsByCategory[category] || []).forEach(t => selected.add(t._id || t.id));
    setPrescriptionData({ ...prescriptionData, tests: Array.from(selected) });
  };

  const clearCategory = (category) => {
    const idsInCat = new Set((testsByCategory[category] || []).map(t => t._id || t.id));
    const next = (prescriptionData.tests || []).filter(id => !idsInCat.has(id));
    setPrescriptionData({ ...prescriptionData, tests: next });
  };

  const onAddImagingTest = () => {
    setPrescriptionData({
      ...prescriptionData,
      imagingTests: [
        ...(prescriptionData.imagingTests || []),
        { test: '', selectedViews: [], instructions: '' }
      ]
    });
  };

  const onRemoveImagingTest = (index) => {
    const newTests = prescriptionData.imagingTests.filter((_, i) => i !== index);
    setPrescriptionData({ ...prescriptionData, imagingTests: newTests });
  };

  // Helper: recursively scan object to find first value whose key matches any regex
  const deepFindFirst = (root, keyMatchers, maxDepth = 4) => {
    try {
      if (!root || typeof root !== 'object') return null;
      const queue = [{ obj: root, path: [], depth: 0 }];
      const visited = new Set();
      const isMatch = (key) => keyMatchers.some(rx => rx.test(key));
      while (queue.length) {
        const { obj, path, depth } = queue.shift();
        if (!obj || typeof obj !== 'object') continue;
        if (visited.has(obj)) continue;
        visited.add(obj);
        for (const key of Object.keys(obj)) {
          const next = obj[key];
          const nextPath = path.concat(key);
          if (isMatch(key)) {
            return { value: next, path: nextPath };
          }
          if (depth < maxDepth && typeof next === 'object' && next !== null) {
            queue.push({ obj: next, path: nextPath, depth: depth + 1 });
          }
        }
      }
      return null;
    } catch (e) {
      console.warn('[PRESCRIPTION_TAB] deepFindFirst failed:', e);
      return null;
    }
  };

  const normalizeGenderToken = (value) => {
    if (value === undefined || value === null) return '';
    if (typeof value === 'object') {
      const candidates = [value.label, value.name, value.value, value.display];
      for (const c of candidates) {
        const v = normalizeGenderToken(c);
        if (v) return v;
      }
      return '';
    }
    const t = String(value).trim();
    if (!t) return '';
    const l = t.toLowerCase();
    if (['m', 'male', 'm.', 'man'].includes(l)) return 'Male';
    if (['f', 'female', 'f.', 'woman'].includes(l)) return 'Female';
    if (['o', 'other', 'others', 'non-binary', 'nonbinary', 'nb'].includes(l)) return 'Other';
    return t.charAt(0).toUpperCase() + t.slice(1);
  };

  const handleGeneratePDF = async () => {
    try {
      // Get selected slogan object
      const selectedSlogan = sloganOptions?.find(slogan => slogan._id === prescriptionData.sloganRef);
      // Prepare doctor data from logged-in user
      const doctorData = getDoctorData();
      console.log('[PRESCRIPTION_TAB][PDF] Doctor info:', doctorData);

      // Helper: fetch full patient if queue payload lacks demographics
      const getEnrichedPatient = async (p) => {
        try {
          if (!p || !p._id) return p;
          const hasDemo = Boolean(p.sex) || Boolean(p.dob) || Boolean(p.dateOfBirth);
          if (hasDemo) return p;
          const resp = await patientAPI.getById(p._id);
          const full = resp?.data?.patient || resp?.patient || resp;
          return full ? { ...p, ...full } : p;
        } catch (e) {
          console.warn('[PRESCRIPTION_TAB] Failed to fetch full patient for enrichment:', e);
          return p;
        }
      };

      // Properly format patient data with all fields
      const patient = await getEnrichedPatient(selectedPatient?.patient);
      // Attempt deep detection for gender/dob/age
      const genderHit = deepFindFirst(patient, [/(sex|gender|genderIdentity|genderDisplay)/i]);
      const dobHit = deepFindFirst(patient, [/(dob|date[_ ]?of[_ ]?birth|birthDate|birth[_ ]?date)/i]);
      const ageHit = deepFindFirst(patient, [/(^age$|ageYears|age_years|patientAge|years|yrs)/i]);
      const patientData = {
        firstName: patient?.firstName || patient?.name || '',
        lastName: patient?.lastName || '',
        sex: patient?.sex || patient?.gender || normalizeGenderToken(genderHit?.value) || '',
        gender: patient?.sex || patient?.gender || normalizeGenderToken(genderHit?.value) || '',
        genderDisplay: patient?.genderDisplay || patient?.sex || patient?.gender || normalizeGenderToken(genderHit?.value),
        genderIdentity: patient?.genderIdentity,
        mobile: patient?.mobile || patient?.phone || patient?.contact?.mobile || patient?.contact?.phone || '',
        phone: patient?.phone || patient?.mobile || patient?.contact?.phone || '',
        contactNumber: patient?.contactNumber || patient?.mobile || patient?.phone || patient?.contact?.mobile || patient?.contact?.phone || '',
        age: patient?.age ?? patient?.basicInfo?.age ?? ageHit?.value ?? '',
        ageInfo: patient?.ageInfo,
        dob: patient?.dob || patient?.dateOfBirth || patient?.birthDate || patient?.basicInfo?.dob || patient?.basicInfo?.dateOfBirth || dobHit?.value || '',
        dateOfBirth: patient?.dateOfBirth || patient?.dob || patient?.birthDate || patient?.basicInfo?.dateOfBirth || dobHit?.value || '',
        basicInfo: patient?.basicInfo || null,
        debugPaths: {
          genderPath: genderHit?.path?.join('.') || null,
          dobPath: dobHit?.path?.join('.') || null,
          agePath: ageHit?.path?.join('.') || null
        }
      };

      // Build prescription data matching PatientDetails.jsx format
      // Include vitals, complaints from appointment if available
      const appointmentData = selectedPatient?.appointment || selectedPatient || {};
      const fullPrescriptionData = {
        ...prescriptionData,
        // Add clinical context like PatientDetails does
        appointmentDate: appointmentData?.dateTime || appointmentData?.appointmentDate || new Date().toISOString(),
        vitals: appointmentData?.vitals || {},
        chiefComplaint: appointmentData?.complaint || appointmentData?.chiefComplaint || '',
        physicalExaminationFindings: appointmentData?.physicalExaminationFindings || appointmentData?.examination || ''
      };

      const result = await generatePDF(
        fullPrescriptionData,
        patientData,
        selectedSlogan,
        prescriptionData.flags?.duplicatePrint || false,
        {
          testsRecords: tests,
          imagingTestsRecords: imagingTests,  // Add imagingTestsRecords like PatientDetails
          doctorData
        }
      );

      if (result.success) {
        alert(`PDF generated successfully: ${result.fileName}`);
      } else {
        alert(`Error generating PDF: ${result.error}`);
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  return (
    <div className="space-y-4" style={{ fontFamily: 'Poppins' }}>
      {/* Current Prescription Form */}
      <div className="bg-white rounded-xl border-2 border-gray-200 shadow-sm overflow-hidden" >
        {/* Header */}
        <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold text-white"style={{ fontFamily: 'sans-serif' }}>New Prescription</h3>
            </div>
            {selectedPatient && (
              <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2">
                <p className="text-white/90 text-xs font-medium">Patient</p>
                <p className="text-white font-semibold">
                  {selectedPatient?.patient?.firstName} {selectedPatient?.patient?.lastName}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="p-6">
          {/* Quick Settings Bar */}
          <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-xl p-4 mb-6 border border-gray-200" style={{ fontFamily: 'Poppins' }}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Language</label>
                <select
                  className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-medium focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all bg-white hover:border-[#2D869C]"
                  value={prescriptionData.language}
                  onChange={(e) => setPrescriptionData({ ...prescriptionData, language: e.target.value })}
                >
                  <option value="E">English</option>
                  <option value="H" disabled className="text-gray-400">Hindi (Coming Soon)</option>
                  <option value="M" disabled className="text-gray-400">Marathi (Coming Soon)</option>
                  <option value="G" disabled className="text-gray-400">Gujarati (Coming Soon)</option>
                </select>
              </div>
              <div>
                <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Patient Type</label>
                <select
                  className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-medium focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all bg-white hover:border-[#2D869C]"
                  value={prescriptionData.adultChild}
                  onChange={(e) => setPrescriptionData({ ...prescriptionData, adultChild: e.target.value })}
                >
                  <option value="adult">Adult</option>
                  <option value="child">Child</option>
                </select>
              </div>
              <div>
                <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Appointment Type</label>
                <select
                  className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-medium focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all bg-white hover:border-[#2D869C]"
                  value={prescriptionData.appointmentType || 'New'}
                  onChange={(e) => setPrescriptionData({ ...prescriptionData, appointmentType: e.target.value })}
                >
                  <option value="New">New Appointment</option>
                  <option value="Follow-up">Follow-up Visit</option>
                </select>
              </div>
            </div>
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Diagnosis */}
              <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-4 py-3">
                  <h4 className="text-xl font-bold text-white" style={{ fontFamily: 'sans-serif' }}>Diagnosis</h4>
                </div>
                <div className="p-4">
                  <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Known Case Disease</label>
                  <div className="relative" ref={diseaseWrapperRef}>
                    <input
                      type="text"
                      className="w-full block border-2 border-gray-200 text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all"
                      placeholder="Type at least 3 characters to search diseases..."
                      value={diseaseInput}
                      onChange={(e) => handleDiseaseInputChange(e.target.value)}
                      onFocus={() => diseaseInput.length >= 3 && setShowDiseaseList(true)}
                    />
                    {diseaseInput && (
                      <button
                        type="button"
                        onClick={() => {
                          setDiseaseInput('');
                          setDiseaseResults([]);
                          setPrescriptionData({ ...prescriptionData, knownCaseDisease: '' });
                          setShowDiseaseList(false);
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                    
                    {showDiseaseList && diseaseInput.length >= 3 && (
                      <div 
                        className="fixed z-[9999] max-h-60 overflow-auto bg-white border-2 border-gray-200 rounded-xl shadow-xl"
                        style={{
                          top: diseaseWrapperRef.current ? `${diseaseWrapperRef.current.getBoundingClientRect().bottom + 8}px` : 'auto',
                          left: diseaseWrapperRef.current ? `${diseaseWrapperRef.current.getBoundingClientRect().left}px` : 'auto',
                          width: diseaseWrapperRef.current ? `${diseaseWrapperRef.current.getBoundingClientRect().width}px` : 'auto'
                        }}
                      >
                        {isDiseaseSearching ? (
                          <div className="px-4 py-3 text-sm text-gray-500 text-center">Searching...</div>
                        ) : diseaseResults.length > 0 ? (
                          diseaseResults.map((disease) => (
                            <button
                              key={disease._id}
                              type="button"
                              className="w-full text-left px-4 py-3 text-sm hover:bg-[#9DC9D4]/20 border-b border-gray-100 last:border-0 transition-colors"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setDiseaseInput(disease.name);
                                setPrescriptionData({ ...prescriptionData, knownCaseDisease: disease._id });
                                setShowDiseaseList(false);
                                setDiseaseResults([]);
                              }}
                            >
                              <div className="text-md font-semibold text-gray-600  uppercase tracking-wide">{disease.name}</div>
                              {disease.code && <div className="text-md font-semibold text-gray-400 uppercase tracking-wide">{disease.code}</div>}
                            </button>
                          ))
                        ) : (
                          <div className="px-4 py-3 text-sm text-gray-500 text-center">No diseases found</div>
                        )}
                      </div>
                    )}
                  </div>
                  
                </div>
              </div>

               {/* Laboratory Tests */}
              <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-4 py-3">
                  <h4 className="text-xl font-bold text-white" style={{ fontFamily: 'sans-serif' }}>Laboratory Tests</h4>
                </div>
                <div className="p-4">
                  <input
                    type="text"
                    value={testSearch}
                    onChange={(e) => setTestSearch(e.target.value)}
                    className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md uppercase font-semibold mb-4 focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all bg-white"
                    placeholder="🔍 Search tests by name or code"
                    disabled={loadingMasterData}
                  />
                  <div className="max-h-96 overflow-auto space-y-2 custom-scrollbar">
                    {Object.keys(testsByCategory).sort((a, b) => a.localeCompare(b)).map(cat => {
                      const testsInCat = testsByCategory[cat];
                      const idsInCat = new Set(testsInCat.map(t => t._id || t.id));
                      const selected = new Set(prescriptionData.tests || []);
                      const selectedCount = testsInCat.filter(t => selected.has(t._id || t.id)).length;
                      const allSelected = selectedCount === idsInCat.size && idsInCat.size > 0;
                      return (
                        <div key={cat} className="border-2 border-gray-200 rounded-lg overflow-hidden bg-white">
                          <div className="flex items-center justify-between px-3 py-2.5 bg-gray-50 border-b border-gray-200">
                            <button type="button" className="flex items-center gap-2 text-left flex-1" onClick={() => toggleCategory(cat)}>
                              <svg
                                className={`w-4 h-4 text-[#2D869C] transition-transform flex-shrink-0 ${isCollapsed(cat) ? '' : 'rotate-90'}`}
                                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                              <div className="block text-md font-semibold text-gray-600 uppercase tracking-wide" style={{ fontFamily: 'sans-serif' }}>{cat}</div>
                              <span className="text-xs text-gray-600 bg-gray-200 px-2 py-0.5 rounded-full font-medium">{selectedCount}/{idsInCat.size}</span>
                            </button>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                className="text-sm px-2.5 py-1 rounded-lg bg-white border border-gray-300 hover:bg-[#9DC9D4]/20 hover:border-[#2D869C] transition-colors font-medium"
                                onClick={() => selectAllInCategory(cat)}
                                disabled={loadingMasterData || allSelected}
                              >All</button>
                              <button
                                type="button"
                                className="text-sm px-2.5 py-1 rounded-lg bg-white border border-gray-300 hover:bg-red-50 hover:border-red-300 transition-colors font-medium"
                                onClick={() => clearCategory(cat)}
                                disabled={loadingMasterData || selectedCount === 0}
                              >Clear</button>
                            </div>
                          </div>
                          {!isCollapsed(cat) && (
                            <div className="p-2 space-y-1">
                              {testsInCat.map(test => {
                                const id = test._id || test.id;
                                const checked = (prescriptionData.tests || []).includes(id);
                                return (
                                  <label key={id} className="flex items-center gap-2.5 text-sm py-2 px-2 hover:bg-[#9DC9D4]/10 rounded-lg cursor-pointer transition-colors">
                                    <input
                                      type="checkbox"
                                      className="rounded text-[#2D869C] focus:ring-[#9DC9D4] border-gray-300 w-4 h-4"
                                      checked={checked}
                                      onChange={(e) => {
                                        const sel = new Set(prescriptionData.tests || []);
                                        if (e.target.checked) sel.add(id); else sel.delete(id);
                                        setPrescriptionData({ ...prescriptionData, tests: Array.from(sel) });
                                      }}
                                    />
                                    <span className="block text-md font-semibold text-gray-600 uppercase tracking-wide">{test.name}</span>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {Object.keys(testsByCategory).length === 0 && (
                      <div className="text-sm text-gray-500 py-8 text-center bg-gray-50 rounded-lg">
                        {loadingMasterData ? 'Loading tests...' : 'No tests found'}
                      </div>
                    )}
                  </div>
                  {/* Selected tests chips */}
                  {Array.isArray(prescriptionData.tests) && prescriptionData.tests.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t-2 border-gray-200">
                      <span className="text-md font-semibold text-gray-600 self-center">Selected ({prescriptionData.tests.length}):</span>
                      {prescriptionData.tests.map(id => {
                        const t = (tests || []).find(x => (x._id || x.id) === id);
                        return (
                          <span key={id} className="inline-flex items-center gap-1.5 bg-gradient-to-r from-[#9DC9D4]/30 to-[#9DC9D4]/20 text-[#2D869C] border border-[#9DC9D4] px-3 py-1.5 rounded-full text-xs font-semibold">
                            <span>{t?.name || id}</span>
                            <button
                              type="button"
                              onClick={() => setPrescriptionData({ ...prescriptionData, tests: (prescriptionData.tests || []).filter(x => x !== id) })}
                              className="hover:text-[#236b7a] font-bold text-base"
                            >×</button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>


              {/* Imaging Tests Section */}
              <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-4 py-3 flex items-center justify-between">
                  <h4 className="text-xl font-bold text-white" style={{ fontFamily: 'sans-serif' }}>Imaging Tests</h4>
                  <button
                    onClick={onAddImagingTest}
                    className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                    Add
                  </button>
                </div>
                <div className="p-4 space-y-4">
                  {(prescriptionData.imagingTests && prescriptionData.imagingTests.length > 0 ? prescriptionData.imagingTests : [{ test: '', selectedViews: [], instructions: '' }]).map((item, index) => {
                    const testId = typeof item === 'object' ? item.test : item;
                    const itemTest = (imagingTests || []).find(t => (t._id || t.id) === testId);
                    const selectedViews = typeof item === 'object' ? (item.selectedViews || []) : [];
                    const instructions = typeof item === 'object' ? (item.instructions || '') : '';

                    // Store modality in the item object for proper selection
                    const currentModality = (typeof item === 'object' && item.modality) || itemTest?.modality || '';

                    // Get available modalities
                    const modalities = [...new Set((imagingTests || []).map(t => t.modality))].filter(Boolean);

                    // Get tests for selected modality only
                    const availableTests = currentModality
                      ? (imagingTests || []).filter(t => t.modality === currentModality)
                      : [];

                    // Get standard views for selected test
                    const standardViews = itemTest?.standardViews || [];

                    return (
                      <div key={index} className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
                          {/* Modality Dropdown */}
                          <div className="md:col-span-4">
                            <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Modality</label>
                            <select
                              className="w-full text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all"
                              value={currentModality}
                              onChange={(e) => {
                                const newModality = e.target.value;
                                const newTests = [...(prescriptionData.imagingTests || [])];
                                newTests[index] = {
                                  ...(typeof newTests[index] === 'object' ? newTests[index] : {}),
                                  modality: newModality,
                                  test: '',
                                  selectedViews: [],
                                  instructions: typeof item === 'object' ? item.instructions : ''
                                };
                                setPrescriptionData({ ...prescriptionData, imagingTests: newTests });
                              }}
                              disabled={loadingMasterData}
                            >
                              <option value="" className="text-md font-semibold text-gray-600 uppercase tracking-wide">Select Modality</option>
                              {modalities.sort().map(modality => (
                                <option key={modality} value={modality} className="text-md font-semibold text-gray-600 uppercase tracking-wide">{modality}</option>
                              ))}
                            </select>
                          </div>

                          {/* Test Dropdown */}
                          <div className="md:col-span-8">
                            <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Test</label>
                            <select
                              className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-md font-semibold text-gray-600 uppercase tracking-wide focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all"
                              value={testId}
                              onChange={(e) => {
                                const selectedTestId = e.target.value;
                                const selectedTest = availableTests.find(t => (t._id || t.id) === selectedTestId);
                                const newTests = [...(prescriptionData.imagingTests || [])];
                                newTests[index] = {
                                  ...(typeof newTests[index] === 'object' ? newTests[index] : {}),
                                  modality: currentModality,
                                  test: selectedTestId,
                                  selectedViews: [],
                                  instructions: selectedTest?.specialInstructions || ''
                                };
                                setPrescriptionData({ ...prescriptionData, imagingTests: newTests });
                              }}
                              disabled={!currentModality || loadingMasterData}
                            >
                              <option value="" className="text-md font-semibold text-gray-600 uppercase tracking-wide">Select Test</option>
                              {availableTests.map(test => {
                                const id = test._id || test.id;
                                return (
                                  <option key={id} value={id} className="text-md font-semibold text-gray-600 uppercase tracking-wide">
                                    {test.testName}
                                  </option>
                                );
                              })}
                            </select>
                          </div>
                        </div>

                        {/* Views Selection */}
                        {testId && standardViews.length > 0 && (
                          <div className="mb-4 mt-8">
                            <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide" style={{ fontFamily: 'sans-serif' }}  >Select Views</label>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                              {standardViews.map((view, viewIdx) => (
                                <label key={viewIdx} className="flex items-center gap-2 text-sm">
                                  <input
                                    type="checkbox"
                                    className="rounded text-[#2D869C] focus:ring-[#9DC9D4] border-gray-300"
                                    checked={selectedViews.includes(view)}
                                    onChange={(e) => {
                                      const newTests = [...(prescriptionData.imagingTests || [])];
                                      const currentViews = typeof newTests[index] === 'object' ? (newTests[index].selectedViews || []) : [];
                                      if (e.target.checked) {
                                        newTests[index] = {
                                          ...(typeof newTests[index] === 'object' ? newTests[index] : { test: testId }),
                                          selectedViews: [...currentViews, view]
                                        };
                                      } else {
                                        newTests[index] = {
                                          ...(typeof newTests[index] === 'object' ? newTests[index] : { test: testId }),
                                          selectedViews: currentViews.filter(v => v !== view)
                                        };
                                      }
                                      setPrescriptionData({ ...prescriptionData, imagingTests: newTests });
                                    }}
                                  />
                                  <span className="block text-md font-semibold text-gray-600 uppercase tracking-wide" style={{ fontFamily: 'sans-serif' }}>{view}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Default Instructions Display */}
                        {testId && itemTest?.specialInstructions && !instructions && (
                          <div className="mb-4">
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                              <div className="block text-md font-semibold text-blue-900 mb-2 uppercase tracking-wide" style={{ fontFamily: 'sans-serif' }}>Default Instructions:</div>
                              <div className="block text-md text-blue-800" style={{ fontFamily: 'sans-serif' }}>{itemTest.specialInstructions}</div>
                            </div>
                          </div>
                        )}

                        {/* Instructions */}
                        <div className=" mt-8">
                          <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide" style={{ fontFamily: 'sans-serif' }}>
                            Special Instructions
                            
                          </label>
                          <textarea
                            className="w-full text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide border-2 border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all"
                            rows="3"
                            value={instructions}
                            onChange={(e) => {
                              const newTests = [...(prescriptionData.imagingTests || [])];
                              newTests[index] = {
                                ...(typeof newTests[index] === 'object' ? newTests[index] : { test: testId }),
                                modality: currentModality,
                                test: testId,
                                selectedViews: selectedViews,
                                instructions: e.target.value
                              };
                              setPrescriptionData({ ...prescriptionData, imagingTests: newTests });
                            }}
                            placeholder={itemTest?.specialInstructions || "Add any special instructions..."}
                          />
                        </div>

                        {/* Remove Button */}
                        {prescriptionData.imagingTests.length > 1 && (
                          <button
                            type="button"
                            onClick={() => onRemoveImagingTest(index)}
                            className="text-red-600 hover:text-red-800 text-sm font-medium flex items-center gap-1.5 hover:underline transition-all"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Remove Imaging Test
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
             
            </div>


            {/* Right Column */}
            <div className="space-y-6">
             

               <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-4 py-3 flex items-center justify-between">
                  <h4 className="text-xl font-bold text-white" style={{ fontFamily: 'sans-serif' }}>Medications</h4>
                  <button
                    onClick={onAddMedication}
                    className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                    Add
                  </button>
                </div>
                <div className="p-4 space-y-3">
                  {prescriptionData.items.map((item, index) => (
                    <MedicationItem
                      key={index}
                      item={item}
                      index={index}
                      onUpdate={onUpdateMedication}
                      onRemove={onRemoveMedication}
                      canRemove={prescriptionData.items.length > 0}
                      medicines={medicines || []}
                      loadingMedicines={loadingMasterData}
                    />
                  ))}
                </div>
              </div>

              {/* Food Advice & Slogan */}
              <div className="grid grid-cols-1 gap-6">
                <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-4 py-3">
                    <h4 className="text-xl font-bold text-white" style={{ fontFamily: 'sans-serif' }}>Food Advice</h4>
                  </div>
                  <div className="p-4">
                    <textarea
                      className="w-full block text-md font-semibold text-gray-600 mb-2 tracking-wide  border-2 border-gray-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all resize-none"
                      rows="4"
                      value={prescriptionData.foodAdvice}
                      onChange={(e) => setPrescriptionData({ ...prescriptionData, foodAdvice: e.target.value })}
                      placeholder="Enter dietary recommendations and food advice for the patient..."
                    />
                  </div>
                </div>

                <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-4 py-3">
                    <h4 className="text-xl font-bold text-white" style={{ fontFamily: 'sans-serif' }}>Slogan</h4>
                  </div>
                  <div className="p-4">
                    <div className="relative" ref={setSloganBoxRef}>
                      <input
                        type="text"
                        className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-semibold text-gray-600 uppercase tracking-wide focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all pr-10"
                        placeholder="Type to select, create, or edit slogan"
                        value={sloganText}
                        onFocus={() => setShowSloganDropdown(true)}
                        onChange={(e) => { setSloganText(e.target.value); setShowSloganDropdown(true); }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); commitSlogan(); setShowSloganDropdown(false); }
                          if (e.key === 'Escape') { e.preventDefault(); setShowSloganDropdown(false); }
                        }}
                        disabled={loadingMasterData || savingSlogan || loadingSlogans}
                      />
                      <svg className="w-4 h-4 text-[#2D869C] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                      {showSloganDropdown && (
                        <div className="fixed z-[9999] max-h-60 overflow-auto bg-white border-2 border-gray-200 rounded-xl shadow-xl" style={{
                          top: sloganBoxRef?.getBoundingClientRect?.()?.bottom ? `${sloganBoxRef.getBoundingClientRect().bottom + 8}px` : 'auto',
                          left: sloganBoxRef?.getBoundingClientRect?.()?.left ? `${sloganBoxRef.getBoundingClientRect().left}px` : 'auto',
                          width: sloganBoxRef?.getBoundingClientRect?.()?.width ? `${sloganBoxRef.getBoundingClientRect().width}px` : 'auto'
                        }}>
                          {(() => {
                            const q = (sloganText || '').toLowerCase().trim();
                            const items = (sloganOptions || []).filter(s => (s.slogan || '').toLowerCase().includes(q));
                            const exact = (sloganOptions || []).find(s => (s.slogan || '').toLowerCase().trim() === q);
                            const canCreate = q && !exact;
                            const canUpdate = q && currentSlogan && (currentSlogan.slogan || '').trim().toLowerCase() !== q;
                            return (
                              <>
                                {canCreate && (
                                  <button
                                    type="button"
                                    className="w-full text-left px-4 py-3 text-md font-semibold text-[#2D869C] uppercase tracking-wide hover:bg-[#9DC9D4]/20 border-b border-gray-100 transition-colors"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={async () => { await commitSlogan(); setShowSloganDropdown(false); }}
                                  >+ Create "{sloganText}"</button>
                                )}
                                {canUpdate && (
                                  <button
                                    type="button"
                                    className="w-full text-left px-4 py-3 text-md font-semibold text-[#2D869C] uppercase tracking-wide hover:bg-[#9DC9D4]/20 border-b border-gray-100 transition-colors"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={async () => { await commitSlogan(); setShowSloganDropdown(false); }}
                                  >✎ Update to "{sloganText}"</button>
                                )}
                                {items.map(s => (
                                  <button
                                    key={s._id}
                                    type="button"
                                    className="w-full text-left px-4 py-3 text-md hover:bg-[#9DC9D4]/20 border-b border-gray-100 last:border-0 transition-colors"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => {
                                      setSloganText(s.slogan || '');
                                      if (prescriptionData.sloganRef !== s._id) setPrescriptionData({ ...prescriptionData, sloganRef: s._id });
                                      setShowSloganDropdown(false);
                                    }}
                                  >
                                    <div className="text-md font-semibold text-gray-600 uppercase tracking-wide">{s.slogan}</div>
                                  </button>
                                ))}
                                {(!items || items.length === 0) && !canCreate && (
                                  <div className="px-4 py-3 text-sm text-gray-500 text-center">No slogans found</div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-2 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Press Enter or click outside to save
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>


          {/* Action Buttons */}  
          <div className="flex items-center justify-between gap-4 pt-6 border-t-2 border-gray-200 mt-6">
            <div className="text-sm text-gray-600">
              
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleClearPrescription}
                disabled={loading}
                className="flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-gray-700 px-6 py-3 rounded-xl font-semibold transition-all border-2 border-gray-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span className="text-xl font-bold text-gray-700" style={{ fontFamily: 'sans-serif' }}>Clear Form</span>
              </button>

              <button
                onClick={onSavePrescription}
                disabled={!selectedPatient || loading}
                className="flex items-center justify-center gap-3 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] hover:from-[#236b7a] hover:to-[#2D869C] disabled:from-gray-400 disabled:to-gray-400 text-white px-4 py-3 rounded-xl font-bold transition-all text-sm shadow-lg hover:shadow-xl disabled:cursor-not-allowed transform hover:scale-105 disabled:transform-none"
              >
                <svg className="w-7 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-xl font-bold text-white" style={{ fontFamily: 'sans-serif' }}>{loading ? 'Saving...' : 'Save Prescription'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Previous Prescriptions */}
      <PreviousPrescriptions
        selectedPatient={selectedPatient}
        previousPrescriptions={previousPrescriptions}
        loadingPrescriptions={loadingPrescriptions}
        tests={tests}
      />
    </div>
  );
};

export default PrescriptionTab;
